import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, from, Observable, of, throwError } from 'rxjs';
import { tap, catchError, finalize, shareReplay, map } from 'rxjs/operators';
import { AccountMemberRole, IAccount, IAccountMember, IImageTemplateSettings } from '../models/account.interface';
import { FacebookPageSelectionResponse } from '../models/facebook/facebook-page-selection-response.interface';
import { FacebookPagesResponse } from '../models/facebook/facebook-pages-response.interface';
import { BlueskyProfile } from '../models/bluesky/bluesky-profile.interface';
import { ToneObject } from '../models/openai/tone-map';
import { CapacitorHttp } from '@capacitor/core';
import { IUser } from '../models/user.interface';
import { UserService } from './user.service';

interface WordPressConnectStartRequest {
  mode: 'self-hosted' | 'wpcom';
  siteUrl?: string;
  appPassword?: string;
}

interface OAuthStartResponse {
  authorizationURL: string;
}

interface WordPressSiteValidationResponse {
  isWordPress: boolean;
  error?: string;
}


@Injectable({
  providedIn: 'root'
})
export class AccountService {
  private accounts$ = new BehaviorSubject<IAccount[] | null>(null);
  private activeAccount$ = new BehaviorSubject<IAccount | null>(this.loadActiveAccountFromStorage());
  private selectingAccount$ = new BehaviorSubject<boolean>(false);
  // Hold an in-flight observable when a network request to fetch accounts is running
  private inFlight$: Observable<IAccount[] | null> | null = null;
  private userService: UserService;

  constructor(private http: HttpClient, private injector: Injector) {
    // Break circular dependency by lazy-injecting UserService
    setTimeout(() => this.userService = this.injector.get(UserService));
  }

  /**
   * Fetch installed fonts and common mapping from the API
   */
  getFonts(): Observable<any> {
    const url = `${environment.apiUrl}/fonts`;
    return this.http.get(url).pipe(
      catchError((err) => {
        console.error('Failed to load fonts', err);
        return of({ installedFamilies: [], commonFontMap: {}, resolved: {} });
      })
    );
  }

  /**
   * Re-check Instagram publishing capabilities for an account.
   * @param accountId The ID of the account.
   */
  recheckInstagramCapabilities(accountId: string): Observable<any> {
    const url = `${environment.apiUrl}/accounts/${accountId}/instagram/recheck-capabilities`;
    return this.http.post(url, {}).pipe(
      tap((res: any) => {
        if (res?.success && res?.account) {
          this.setActiveAccountLocal(res.account);
        }
      }),
      catchError((err) => {
        console.error('Failed to re-check Instagram capabilities', err);
        throw err;
      })
    );
  }

  /** Set the active account locally in cache and localStorage (no network) */
  setActiveAccountLocal(account: IAccount | null): void {
    this.selectingAccount$.next(false);
    if (account) {
      try { localStorage.setItem('activeAccountId', account._id); } catch (e) { console.warn('Unable to persist activeAccountId', e); }
      if (!account.publishTargets) {
        account.publishTargets = {};
      }
    } else {
      try { localStorage.removeItem('activeAccountId'); } catch (e) {/* ignore */ }
    }

  const current = this.activeAccount$.value;
  const requestedId = account && account._id ? account._id : null;

  // If same id, merge to avoid losing fields. Prefer incoming values but preserve nested objects.
  if (requestedId && current && current._id === requestedId) {
    // Shallow merge
    const merged: IAccount = { ...current, ...(account || {}) };

    // Use incoming publishTargets if present (overwrite existing)
    // If account.publishTargets is defined, it replaces current completely
    if (account && account.publishTargets) {
      merged.publishTargets = account.publishTargets;
    } else {
      // If not present in update, keep existing
      merged.publishTargets = current.publishTargets || {};
    }

    // If other nested objects need preservation (e.g., settings), merge similarly.

    // Emit a cloned object to avoid accidental shared references
    this.activeAccount$.next(JSON.parse(JSON.stringify(merged)));
    return;
  }

  // No merge needed (different id or null) — set directly but clone if coming from mutable source
  this.activeAccount$.next(account ? JSON.parse(JSON.stringify(account)) : null);
}

  /** Create a new account for the current user */
  createAccount(payload: { name: string }): Observable<IAccount | null> {
    const url = `${environment.apiUrl}/accounts`;
    return this.http.post<{ account: IAccount }>(url, payload).pipe(
      map(response => response.account),
      tap((acct) => {
        if (!acct) return;

        // This is the key fix:
        // Persist the new account as the active one on the backend.
        // The UserService will then call back to AccountService's setActiveAccountLocal
        // to update the local state and localStorage.
        if (this.userService) {
          this.userService.selectActiveAccount(acct).subscribe();
        } else {
          // Fallback for the brief moment before lazy injection completes
          this.setActiveAccountLocal(acct);
        }

        // append to local cache if present
        const current = this.accounts$.value || [];
        this.accounts$.next([...(current || []), acct]);
        
        // Refresh accounts from server to pick up any server-populated fields
        this.getAccounts(true).toPromise().catch(()=>{});
      }),
      catchError((err) => {
        console.error('Failed to create account', err);
        return of(null);
      })
    );
  }

  /** Hydrate the accounts cache from an external source (e.g., initial API response) */
  hydrateAccounts(accounts: IAccount[] | null) {
    this.accounts$.next(accounts);
    // If there's an activeAccount id stored but no full object, try to select matching account
    const activeId = (() => {
      try { return localStorage.getItem('activeAccountId'); } catch (e) { return null; }
    })();
    if (activeId && accounts) {
      const found = accounts.find(a => a._id === activeId);
  if (found) this.setActiveAccountLocal(found);
    }
  }


  
  /** Load the active account id from localStorage and return a placeholder account object (will be replaced when accounts load) */
  private loadActiveAccountFromStorage(): IAccount | null {
    try {
      const id = localStorage.getItem('activeAccountId');
      if (id) return { _id: id } as IAccount;
    } catch (e) {
      // ignore
    }
    return null;
  }


  /**
   *  Return the currently active account value synchronously 
   */
  getActiveAccount(): IAccount | null {
    return this.activeAccount$.value;
  }




  
  /** Fetch accounts for current user from the API */
  getAccounts(force: boolean = false): Observable<IAccount[] | null> {
    if (!force && this.accounts$.value) {
      return this.accounts$.asObservable();
    }

    // If a request is already in progress, return the same observable so callers share it
    if (this.inFlight$) return this.inFlight$;

    // Build URL and headers; include cache-busting query when force=true to avoid 304 Not Modified responses
    let url = `${environment.apiUrl}/accounts`;
    if (force) {
      const sep = url.includes('?') ? '&' : '?';
      url = `${url}${sep}t=${Date.now()}`;
    }
    const headers = { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' };

    const req$ = this.http.get<IAccount[]>(url, { headers }).pipe(
      tap((accounts) => this.accounts$.next(accounts)),
      catchError((err) => {
        // keep existing value and return null
        console.error('Failed to load accounts', err);
        return of(null);
      }),
      finalize(() => { this.inFlight$ = null; }),
      // ensure multiple subscribers share the same network request and replay last value
      shareReplay(1)
    );

    this.inFlight$ = req$;
    return req$;
  }

  /** Observable of accounts list */
  accountsObservable(): Observable<IAccount[] | null> {
    return this.accounts$.asObservable();
  }

  /** Synchronously return the current cached accounts value (or null) */
  getAccountsValue(): IAccount[] | null {
    return this.accounts$.value;
  }

  /** Observable of currently selected account */
  activeAccountObservable(): Observable<IAccount | null> {
    return this.activeAccount$.asObservable();
  }

  /** Select an account locally (persist in localStorage) and emit */
  // Note: selection persistence is performed by UserService.selectActiveAccount.
  // AccountService is responsible only for local cache/state updates via
  // setActiveAccountLocal() and managing the accounts cache.

  /** Observable for whether the UI is in account-selection mode */
  selectingAccountObservable(): Observable<boolean> {
    return this.selectingAccount$.asObservable();
  }

  /** Set selecting account mode (UI can block navigation while true) */
  setSelectingAccountMode(v: boolean) {
    this.selectingAccount$.next(!!v);
  }

  /** Helper to fetch account by id from cache or API */
  getAccountById(id: string): Observable<IAccount | null> {
    const accounts = this.accounts$.value || [];
    const found = accounts.find(a => a._id === id);
    if (found) return of(found);
    const url = `${environment.apiUrl}/accounts/${id}`;
    return this.http.get<IAccount>(url).pipe(
      catchError((err) => {
        console.error('Failed to load account by id', err);
        return of(null);
      })
    );
  }

  /** Helper to build logo url for an account (if account has logo stored as filename) */
  getAccountLogoUrl(account: IAccount | null): string {
    if (account?.logo) {
      // If the logo is already a full URL, return it
      if (account.logo.startsWith('http') || account.logo.startsWith('//')) return account.logo;
      // Return a client-rooted path so the browser requests the file from the UI server (e.g., http://localhost:8100)
      try {
        const id = account._id || 'unknown';
        return `/assets/accounts/${id}/${account.logo}`;
      } catch (e) {
        // Fallback to a simple client-rooted assets path
        return `/assets/accounts/${account.logo}`;
      }
    }
    return 'assets/logos/social-dash-logo-with-white-circle.png'; // default logo
  }

  /** Update account metadata (name, description, etc.) */
  updateAccount(accountId: string, payload: Partial<IAccount>): Observable<IAccount | null> {
    const url = `${environment.apiUrl}/accounts/${accountId}`;
    // API exposes a PATCH handler for partial updates; use PATCH to avoid 404s
    return this.http.patch<IAccount>(url, payload).pipe(
      tap((acct) => {
        // update cache if present
        const accounts = this.accounts$.value || [];
        const idx = accounts.findIndex(a => a._id === acct._id);
        if (idx >= 0) {
          accounts[idx] = acct;
          this.accounts$.next([...accounts]);
        }
        // also update activeAccount if it matches
        if (this.activeAccount$.value && this.activeAccount$.value._id === acct._id) {
          this.activeAccount$.next(acct);
        }
      }),
      catchError((err) => {
        console.error('Failed to update account', err);
        return of(null);
      })
    );
  }

  /** Upload an account logo file to the API (expects multipart/form-data) */
  uploadAccountLogo(accountId: string, file: File): Observable<{ logo?: string } | null> {
    const url = `${environment.apiUrl}/accounts/${accountId}/logo`;
    const fd = new FormData();
    fd.append('logo', file, file.name);
    return this.http.post<{ logo?: string }>(url, fd).pipe(
      tap((res) => {
        // if logo returned, update local cache
        if (res?.logo) {
          const accounts = this.accounts$.value || [];
          const idx = accounts.findIndex(a => a._id === accountId);
          if (idx >= 0) {
            accounts[idx].logo = res.logo;
            this.accounts$.next([...accounts]);
          }
          if (this.activeAccount$.value && this.activeAccount$.value._id === accountId) {
            const updated = { ...this.activeAccount$.value, logo: res.logo } as IAccount;
            this.activeAccount$.next(updated);
          }
        }
      }),
      catchError((err) => {
        console.error('Failed to upload account logo', err);
        return of(null);
      })
    );
  }

  /** Update a single publish target for an account */
  updatePublishTarget(accountId: string, targetKey: string, publishTarget: any): Observable<IAccount | null> {
    const url = `${environment.apiUrl}/accounts/${accountId}/publish-targets/${targetKey}`;
    return this.http.post<IAccount>(url, publishTarget).pipe(
      tap((acct) => {
        // update cache
        const accounts = this.accounts$.value || [];
        const idx = accounts.findIndex(a => a._id === acct._id);
        if (idx >= 0) { accounts[idx] = acct; this.accounts$.next([...accounts]); }
        if (this.activeAccount$.value && this.activeAccount$.value._id === acct._id) this.activeAccount$.next(acct);
      }),
      catchError((err) => { console.error('Failed to update publish target', err); return of(null); })
    );
  }

  /** Delete a single publish target from an account */
  deletePublishTarget(accountId: string, targetKey: string): Observable<IAccount | null> {
    const url = `${environment.apiUrl}/accounts/${accountId}/publish-targets/${targetKey}`;
    return this.http.delete<IAccount>(url).pipe(
      tap((acct) => {
        const accounts = this.accounts$.value || [];
        const idx = accounts.findIndex(a => a._id === acct._id);
        if (idx >= 0) { accounts[idx] = acct; this.accounts$.next([...accounts]); }
        if (this.activeAccount$.value && this.activeAccount$.value._id === acct._id) this.activeAccount$.next(acct);
      }),
      catchError((err) => { console.error('Failed to delete publish target', err); return of(null); })
    );
  }

  /** Upsert a member's role on the account */
  upsertMember(accountId: string, memberUserId: string, role: string): Observable<IAccount | null> {
    const url = `${environment.apiUrl}/accounts/${accountId}/members/${memberUserId}`;
    return this.http.post<IAccount>(url, { role }).pipe(
      tap((acct) => {
        const accounts = this.accounts$.value || [];
        const idx = accounts.findIndex(a => a._id === acct._id);
        if (idx >= 0) { accounts[idx] = acct; this.accounts$.next([...accounts]); }
        if (this.activeAccount$.value && this.activeAccount$.value._id === acct._id) this.activeAccount$.next(acct);
      }),
      catchError((err) => { console.error('Failed to upsert member', err); return of(null); })
    );
  }

  /** Remove a member from the account */
  removeMember(accountId: string, memberUserId: string): Observable<IAccount | null> {
    const url = `${environment.apiUrl}/accounts/${accountId}/members/${memberUserId}`;
    return this.http.delete<IAccount>(url).pipe(
      tap((acct) => {
        const accounts = this.accounts$.value || [];
        const idx = accounts.findIndex(a => a._id === acct._id);
        if (idx >= 0) { accounts[idx] = acct; this.accounts$.next([...accounts]); }
        if (this.activeAccount$.value && this.activeAccount$.value._id === acct._id) this.activeAccount$.next(acct);
      }),
      catchError((err) => { console.error('Failed to remove member', err); return of(null); })
    );
  }

  /** Invite a collaborator to an account (owner/admin only) - returns the plaintext token so callers can email it */
  inviteToAccount(accountId: string, email: string, role: AccountMemberRole): Observable<any> {
    const url = `${environment.apiUrl}/accounts/${accountId}/invite`;
    return this.http.post<any>(url, { email, role }).pipe(
      catchError((err) => { console.error('Failed to invite to account', err); return throwError(() => err); })
    );
  }

  /** Revoke a pending invite */
  revokeInvite(accountId: string, email: string): Observable<any> {
    const url = `${environment.apiUrl}/accounts/${accountId}/invite`;
    return this.http.request('delete', url, { body: { email } }).pipe(
      catchError((err) => { console.error('Failed to revoke invite', err); return of(null); })
    );
  }

  /** Resend an invite email to a pending invitee */
  resendInvite(accountId: string, email: string): Observable<any> {
    const url = `${environment.apiUrl}/accounts/${accountId}/invite/resend`;
    return this.http.post<any>(url, { email }).pipe(
      catchError((err) => { console.error('Failed to resend invite', err); return of(null); })
    );
  }

 /**
   * Accept an invitation with a token (for logged-in users).
   * @param token The invitation token.
   * @returns The account ID if successful.
   */
  acceptInviteWithToken(token: string): Observable<any> {
    const url = `${environment.apiUrl}/accounts/accept-invite`;
    return from(CapacitorHttp.post({
      url,
      data: { token },
      headers: { 'Content-Type': 'application/json' }
    }));
  }


    /**
   * Get all active accounts for the current user
   */
  getAllActiveAccounts(): Observable<IAccount[]> {
    const url = `${environment.apiUrl}/accounts/active`;
    return this.http.get<IAccount[]>(url).pipe(
      tap((accounts) => {
        // Hydrate accounts cache
        this.accounts$.next(accounts);
        // Also set active account if there's only one
        if (accounts.length === 1) {
          this.setActiveAccountLocal(accounts[0]);
        }
      }),
      catchError((err) => {
        console.error('Failed to load active accounts', err);
        return of([]);
      })
    );
  }

  /** Select an account as the user's default/active account (persists via API) */
  selectAccount(accountId: string): Observable<any> {
    const url = `${environment.apiUrl}/accounts/${accountId}/select`;
    return this.http.post<any>(url, {}).pipe(
      tap((res) => {
        try {
          const acct = res?.activeAccount || null;
          if (acct) this.setActiveAccountLocal(acct as IAccount);
        } catch (e) { /* ignore */ }
      }),
      catchError((err) => { console.error('Failed to select account', err); return of(null); })
    );
  }

  /** Aggregator: update keywords for an account */
  updateAggregatorKeywords(accountId: string, keywords: string[]): Observable<IAccount | null> {
    const url = `${environment.apiUrl}/accounts/${accountId}/aggregator-keywords`;
    return this.http.post<IAccount>(url, keywords).pipe(
      tap((acct) => {
        const accounts = this.accounts$.value || [];
        const idx = accounts.findIndex(a => a._id === acct._id);
        if (idx >= 0) { accounts[idx] = acct; this.accounts$.next([...accounts]); }
        if (this.activeAccount$.value && this.activeAccount$.value._id === acct._id) this.activeAccount$.next(acct);
      }),
      catchError((err) => { console.error('Failed to update aggregator keywords', err); return of(null); })
    );
  }

  /** Aggregator: update categories for an account */
  updateAggregatorCategories(accountId: string, categories: string[]): Observable<IAccount | null> {
    const url = `${environment.apiUrl}/accounts/${accountId}/aggregator-categories`;
    return this.http.post<IAccount>(url, categories).pipe(
      tap((acct) => {
        const accounts = this.accounts$.value || [];
        const idx = accounts.findIndex(a => a._id === acct._id);
        if (idx >= 0) { accounts[idx] = acct; this.accounts$.next([...accounts]); }
        if (this.activeAccount$.value && this.activeAccount$.value._id === acct._id) this.activeAccount$.next(acct);
      }),
      catchError((err) => { console.error('Failed to update aggregator categories', err); return of(null); })
    );
  }

  /** Aggregator: update bias thresholds for an account */
  updateBiasThreshold(accountId: string, biasThresholdMin: number, biasThresholdMax: number): Observable<IAccount | null> {
    const url = `${environment.apiUrl}/accounts/${accountId}/bias`;
    return this.http.post<IAccount>(url, { biasThresholdMin, biasThresholdMax }).pipe(
      tap((acct) => {
        const accounts = this.accounts$.value || [];
        const idx = accounts.findIndex(a => a._id === acct._id);
        if (idx >= 0) { accounts[idx] = acct; this.accounts$.next([...accounts]); }
        if (this.activeAccount$.value && this.activeAccount$.value._id === acct._id) this.activeAccount$.next(acct);
      }),
      catchError((err) => { console.error('Failed to update bias thresholds', err); return of(null); })
    );
  }

  /** Aggregator: update reliability threshold for an account */
  updateReliabilityThreshold(accountId: string, reliabilityThreshold: number): Observable<IAccount | null> {
    const url = `${environment.apiUrl}/accounts/${accountId}/reliability`;
    return this.http.post<IAccount>(url, { reliabilityThreshold }).pipe(
      tap((acct) => {
        const accounts = this.accounts$.value || [];
        const idx = accounts.findIndex(a => a._id === acct._id);
        if (idx >= 0) { accounts[idx] = acct; this.accounts$.next([...accounts]); }
        if (this.activeAccount$.value && this.activeAccount$.value._id === acct._id) this.activeAccount$.next(acct);
      }),
      catchError((err) => { console.error('Failed to update reliability threshold', err); return of(null); })
    );
  }

  /** Update title examples for an account */
  updateTitleExamples(accountId: string, titleExamples: any[] | null): Observable<IAccount | null> {
    const url = `${environment.apiUrl}/accounts/${accountId}/title-examples`;
    return this.http.put<IAccount>(url, titleExamples).pipe(
      tap((acct) => {
        const accounts = this.accounts$.value || [];
        const idx = accounts.findIndex(a => a._id === acct._id);
        if (idx >= 0) { accounts[idx] = acct; this.accounts$.next([...accounts]); }
        if (this.activeAccount$.value && this.activeAccount$.value._id === acct._id) this.activeAccount$.next(acct);
      }),
      catchError((err) => { console.error('Failed to update title examples', err); return of(null); })
    );
  }


  /**
* Update AI directions
* @param accountId user id
* @param directions directions for AI
* @returns 
*/
  updateDirections(accountId: string, directions: string): Observable<any> {
    const url = `${environment.apiUrl}/accounts/${accountId}/directions`;

    // TODO: Limit chars

    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };

    return this.http.post(url, { directions }, options);
  }


  /**
   * Add a custom tone
   * @param accountId user id
   * @param tone the custom tone object
   * @returns 
   */
  addCustomTone(accountId: string, tone: ToneObject): Observable<any> {
    const url = `${environment.apiUrl}/accounts/${accountId}/tones`;

    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };

    return this.http.post(url, tone, options);
  }


  /**
   * Update existing custom tone
   * @param accountId 
   * @param tone 
   * @returns 
   */
  updateCustomTone(accountId: string, tone: ToneObject): Observable<any> {
    const url = `${environment.apiUrl}/accounts/${accountId}/tones/${tone.role}`;
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };
    return this.http.put(url, tone, options);
  }



  /**
   * Delete a custom tone
   * @param accountId user id
   * @param role the name of the tone to delete
   * @returns 
   */
  deleteCustomTone(accountId: string, role: string): Observable<any> {
    const url = `${environment.apiUrl}/accounts/${accountId}/tones/${role}`;

    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };

    return this.http.delete(url, options);
  }


  /**
   * Update user default tone
   * @param accountId 
   * @param role 
   * @returns 
   */
  updateDefaultTone(accountId: string, role: string): Observable<any> {
    const url = `${environment.apiUrl}/accounts/${accountId}/tones/default`;

    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };

    return this.http.post(url, { role }, options);
  }


  /**
  * Update image template settings
  * @param accountId user id
  * @param imageTemplateSettings image template settings
  * @returns 
  */
  updateImageTemplateSettings(accountId: string, imageTemplateSettings: IImageTemplateSettings): Observable<any> {
    const url = `${environment.apiUrl}/accounts/${accountId}/image-template-settings`;

    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };

    return this.http.post(url, { imageTemplateSettings }, options);
  }


    /**
   * Fetch the list of Facebook pages from the backend (stored in session)
   */
  getFacebookPages(accountId: string): Observable<FacebookPagesResponse> {
    const url = `${environment.apiUrl}/accounts/${accountId}/facebook/pages`;
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      withCredentials: true
    };
    return this.http.get<FacebookPagesResponse>(url, options);
  }


  /**
   * Send the selected Facebook page ID to the backend to finalize linking
   */
  selectFacebookPage(accountId: string,pageId: string): Observable<FacebookPageSelectionResponse> {
    const url = `${environment.apiUrl}/accounts/${accountId}/facebook/select-page`;
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      withCredentials: true
    };
    return this.http.post<FacebookPageSelectionResponse>(url, { pageId }, options);
  }

  /**
   * Validate if a URL is a WordPress site by probing /wp-json endpoint.
   */
  validateWordPressSiteUrl(siteUrl: string): Observable<WordPressSiteValidationResponse> {
    // Normalize URL
    let normalized = siteUrl.trim();
    if (!normalized.startsWith('http://') && !normalized.startsWith('https://')) {
      normalized = 'https://' + normalized;
    }
    try {
      const urlObj = new URL(normalized);
      normalized = urlObj.origin;
    } catch {
      return of({ isWordPress: false, error: 'Invalid URL' });
    }

    const probeUrl = `${normalized}/wp-json/`;
    return this.http.get<any>(probeUrl, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    }).pipe(
      map(response => ({
        isWordPress: true
      })),
      catchError(() => of({ isWordPress: false, error: 'Could not reach WordPress site' }))
    );
  }

  /**
   * Start WordPress connect flow.
   */
  startWordPressConnect(accountId: string, request: WordPressConnectStartRequest): Observable<OAuthStartResponse> {
    const url = `${environment.apiUrl}/oauth/wordpress/${accountId}`;
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };
    return this.http.post<OAuthStartResponse>(url, request, options);
  }


  /**
   * Get Bluesky user profile.
   * @param blueskyUserHandle The user handle.
   */
  getBlueskyUserProfile(blueskyUserHandle: string): Observable<BlueskyProfile> {
    const url = `${environment.apiUrl}/accounts/bluesky/user/${blueskyUserHandle}`;
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };
    return this.http.get<BlueskyProfile>(url, options);
  }

  /**
   * Validates existing Bluesky login credentials for a connected account.
   * @param accountId The ID of the account to validate.
   */
  validateExistingBlueskyLogin(accountId: string): Observable<{ success: boolean }> {
    const url = `${environment.apiUrl}/accounts/bluesky/validate-login`;
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };
    return this.http.post<{ success: boolean }>(url, { accountId }, options);
  }

  /**
   * Validates new, unsaved Bluesky login credentials.
   * @param username The Bluesky username/handle.
   * @param apiKey The Bluesky app password.
   */
  validateNewBlueskyCredentials(username: string, apiKey: string): Observable<{ success: boolean }> {
    const url = `${environment.apiUrl}/accounts/bluesky/validate-login`;
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };
    return this.http.post<{ success: boolean }>(url, { username, apiKey }, options);
  }

    /**
   * Link a Bluesky profile to an account
   * @param accountId The account ID.
   * @param blueskyUserHandle The Bluesky user handle.
   */
  linkBlueskyProfile(accountId: string, blueskyUserHandle: string): Observable<any> {
    const url = `${environment.apiUrl}/accounts/${accountId}/bluesky/link`;
    return this.http.post(url, { blueskyUserHandle }).pipe(
      catchError((err) => { console.error('Failed to link Bluesky profile', err); return of(null); })
    );
  }


     /* Unlink a Bluesky profile from an account
   * @param accountId The account ID.
   */
  unlinkBlueskyProfile(accountId: string): Observable<any> {
    const url = `${environment.apiUrl}/accounts/${accountId}/bluesky/unlink`;
    return this.http.post(url, {}).pipe(
      catchError((err) => { console.error('Failed to unlink Bluesky profile', err); return of(null); })
    );
  }

  /**
   * Get all members of an account with their details.
   * @param accountId The ID of the account.
   */
  getAccountMembers(accountId: string): Observable<IAccountMember[]> {
    const url = `${environment.apiUrl}/accounts/${accountId}/members`;
    return this.http.get<IAccountMember[]>(url).pipe(
      catchError((err) => {
        console.error('Failed to get account members', err);
        return of([]);
      })
    );
  }

  /**
   * Get a list of members eligible to become the new owner of an account.
   * @param accountId The ID of the account.
   */
  getEligibleOwners(accountId: string): Observable<IUser[]> {
    const url = `${environment.apiUrl}/accounts/${accountId}/eligible-owners`;
    return this.http.get<IUser[]>(url).pipe(
      catchError((err) => {
        console.error('Failed to get eligible owners', err);
        return of([]);
      })
    );
  }

  /**
   * Transfer ownership of an account to another member.
   * @param accountId The ID of the account.
   * @param newOwnerId The ID of the new owner.
   */
  transferOwnership(accountId: string, newOwnerId: string): Observable<any> {
    const url = `${environment.apiUrl}/accounts/${accountId}/transfer-ownership`;
    return this.http.post(url, { newOwnerId }).pipe(
      catchError((err) => {
        console.error('Failed to transfer ownership', err);
        throw err;
      })
    );
  }

  /**
   * Permanently delete an account. Only the owner may call this.
   * @param accountId The ID of the account to delete.
   */
  deleteAccount(accountId: string): Observable<void> {
    const url = `${environment.apiUrl}/accounts/${accountId}`;
    return this.http.delete<void>(url).pipe(
      tap(() => {
        const accounts = (this.accounts$.value || []).filter(a => a._id !== accountId);
        this.accounts$.next(accounts);
        if (this.activeAccount$.value && this.activeAccount$.value._id === accountId) {
          this.activeAccount$.next(accounts[0] ?? null);
        }
      }),
      catchError((err) => {
        console.error('Failed to delete account', err);
        throw err;
      })
    );
  }

  /**
   * Update the default AI provider for an account.
   * @param accountId The ID of the account.
   * @param provider The provider to set.
   */
  updateDefaultAiProvider(accountId: string, provider: string): Observable<any> {
    const url = `${environment.apiUrl}/accounts/${accountId}/default-ai-provider`;
    return this.http.put(url, { provider }).pipe(
      tap((res: any) => {
        if (res?.account) {
          this.setActiveAccountLocal(res.account);
        }
      }),
      catchError((err) => {
        console.error('Failed to update default AI provider', err);
        throw err;
      })
    );
  }
}
